import { motion } from "framer-motion";
import { ArrowUpRight, Plus, Star } from "lucide-react";
import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/data/books";

export default function BookCard({ book, delay = 0 }) {
  const { add } = useCart();

  return (
    <motion.article
      data-testid={`book-card-${book.slug}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -8 }}
      className="group relative bg-white/70 border border-sand rounded-[1.75rem] p-5 sm:p-6 flex flex-col transition-shadow duration-300 hover:shadow-[0_28px_60px_-24px_rgba(44,34,30,0.28)]"
    >
      <div className="flex items-start justify-between mb-4">
        <span className="font-mono text-sm text-walnutmuted">{book.index}</span>
        {book.badge && (
          <span className="text-[11px] font-bold uppercase tracking-widest text-terracotta bg-terracotta/10 rounded-full px-3 py-1">
            {book.badge}
          </span>
        )}
      </div>

      <Link to={`/books/${book.slug}`} data-testid={`book-cover-link-${book.slug}`} className="relative block spotlight rounded-2xl overflow-hidden mb-5">
        <motion.img
          src={book.cover}
          alt={`${book.title} book cover`}
          className="w-full aspect-[4/5] object-contain p-6 drop-shadow-[0_18px_24px_rgba(44,34,30,0.25)]"
          whileHover={{ scale: 1.05, rotate: -1.5 }}
          transition={{ type: "spring", stiffness: 200, damping: 18 }}
        />
      </Link>

      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-amberbrand mb-1.5">{book.ages} · {book.format}</p>
      <h3 className="font-display font-bold text-xl sm:text-2xl tracking-tight text-walnut leading-tight mb-2">
        {book.title}
      </h3>
      {book.rating && (
        <p className="flex items-center gap-1.5 text-xs text-walnutsoft mb-3">
          <span className="flex text-amberbrand">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} size={12} fill="currentColor" strokeWidth={0} />
            ))}
          </span>
          {book.rating.toFixed(1)} · {book.reviews} Amazon reviews
        </p>
      )}
      <p className="text-sm text-walnutsoft leading-relaxed line-clamp-3 mb-5">{book.description}</p>

      <div className="mt-auto flex items-center justify-between gap-3">
        <span className="font-display font-extrabold text-2xl text-walnut">{formatPrice(book.price)}</span>
        <div className="flex gap-2">
          <button
            data-testid={`add-to-basket-${book.slug}`}
            onClick={() => add(book.slug)}
            className="w-11 h-11 rounded-full border border-walnut/20 flex items-center justify-center text-walnut hover:bg-walnut hover:text-cream transition-colors duration-300"
            aria-label={`Add ${book.title} to basket`}
          >
            <Plus size={18} />
          </button>
          <Link
            to={`/books/${book.slug}`}
            data-testid={`book-details-${book.slug}`}
            className="h-11 rounded-full bg-terracotta text-cream pl-4 pr-5 flex items-center gap-1.5 text-sm font-semibold hover:bg-terracottadark transition-colors duration-300"
          >
            Discover <ArrowUpRight size={15} />
          </Link>
        </div>
      </div>
    </motion.article>
  );
}
